import EmojisPickerEmoji from 'components/emojisPicker/models/emoji';
import { GridConfig } from 'components/grid/grid';
import { wccModules } from 'enums/wccModules';
import { FilesHelpers } from 'helpers/files';
import { inject, injectable } from 'inversify';
import { PureComputed, Subscribable } from 'knockout';
import { Quill } from 'libraries/quill';
import AutofocusManager from 'managers/autofocus';
import { NodeTracker } from 'managers/nodes/tracker';
import WCCS3ImageUploader from 'managers/uploads/s3/image';
import { EffectsContainer } from 'mixins/withEffects';
import WCCAttachment from 'models/attachments/attachment';
import WCCUpload from 'models/upload';
import { IChatEditorBlot } from './blots/iblot';
import './chat.html';
import { ChatEditorToolbarConfig, ChatEditorToolsGroupConfig, ChatEditorVMConfig, ChatEditorVMTheme, ChatEditorVMToolbarPlacement, IChatEditorVM } from './chat.interfaces';
import './chat.scss';
import { Formatter } from './managers/formatter';
import { QuillWrapper } from './managers/quillWrapper';
import { Tool } from './tools/tool';

const attachmentsFocusAreaSize = 100;

components
    .registerLocal('chat-editor-icon-tool', () => require('./tools/icon/icon'))
    .registerLocal('chat-editor-colorpicker-tool', () => require('./tools/colorpicker/colorpicker'))
    .registerLocal('chat-editor-select-tool', () => require('./tools/select/select'))
    .registerLocal('chat-editor-emojis-tool', () => require('./tools/emojis/emojis'))
    .preload('grid-new', 'attachment-controls', 'wcc-generic-attachment');

interface ChatEditorToolbar {
    left: ChatEditorToolbarSection
    right: ChatEditorToolbarSection
    placement: ChatEditorVMToolbarPlacement
}

interface ChatEditorToolbarSection {
    groups: Array<ChatEditorToolsGroup>

    isVisible: PureComputed<boolean>
    isPinned: PureComputed<boolean>
}

interface ChatEditorToolsGroup {
    tools: Array<Tool>

    css: PureComputed<string>
    isVisible: PureComputed<boolean>
    isPinned: PureComputed<boolean>
}

@injectable()
export default class ChatEditorVM implements IChatEditorVM {
    private imagesUploader = new WCCS3ImageUploader();    
    private changesCount = ko.observable(0);

    private quill: Subscribable<Quill | undefined>    

    bodyNode = ko.observable<HTMLElement>();
    extrasNode = ko.observable<HTMLElement>();
    externalContentNode = ko.observable<HTMLElement>();
    attachmentsNode = ko.observable<HTMLElement>();
    quillNode = ko.observable<HTMLElement>();
    leftToolbarNode = ko.observable<HTMLElement>();
    rightToolbarNode = ko.observable<HTMLElement>();

    isEmpty = ko.observable(true);
    hasText = ko.observable(false);
    hasFocus = ko.observable(false);

    attachments = ko.observableArray<WCCAttachment>().extend({ deferred: true });
    uploads = this.imagesUploader.uploads;
    attachmentsAndUploads = ko.pureComputed(() => (<Array<WCCAttachment | WCCUpload>>this.attachments()).concat(this.uploads()));

    content: Subscribable<string>

    attachmentsGrid: GridConfig<WCCAttachment | WCCUpload>
    toolbar: ChatEditorToolbar
    formatter: Formatter

    minHeight: Subscribable<number>
    maxHeight: Subscribable<number | undefined>
    bodyPadding: Subscribable<string | undefined>
    bodyMargin: Subscribable<string | undefined>

    isReady: Subscribable<boolean>
    hasMedia: Subscribable<boolean>
    hasExternalContent: PureComputed<boolean>

    constructor(
        @inject(wccModules.componentConfig) config: ChatEditorVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        const isAutoFocusEnabled = ko.flattenComputed(config.autofocus, false);
        const theme = ko.pureComputed(() => this.getTheme(config));
        const hasBackground = ko.flattenComputed(config.background, true);

        this.hasFocus(ko.unwrap(config.focus) ?? false);

        const quillWrapper = effects.register(new QuillWrapper({
            node: this.quillNode,
            placeholder: config.placeholder,
            bindings: config.bindings,
            formats: config.formats,
            blots: config.blots,

            onSelectionChange: range => {
                if (range)
                    this.formatter.update();

                this.hasFocus(range != undefined);
            },

            onTextChange: quill => {
                if (this.hasFocus())
                    this.formatter.update();

                const text = quill.getText();

                this.isEmpty(quill.getLength() === 1 && text === '\n');
                this.hasText(text.trim().length > 0);
                this.changesCount.inc();
            }
        }));

        this.quill = quillWrapper.quill;
        this.formatter = new Formatter({ quill: quillWrapper.quill });
        this.toolbar = this.buildToolbar(config.toolbar);

        this.maxHeight = ko.pureComputed(() => {
            const target = ko.unwrap(config.maxHeight);

            if (target && target > 0)
                return target;
        });

        const leftToolbarTracker = new NodeTracker({ node: this.leftToolbarNode });

        const {
            width: leftToolbarWidth,
            height: leftToolbarHeight
        } = leftToolbarTracker;

        const rightToolbarTracker = new NodeTracker({ node: this.rightToolbarNode });

        const {
            width: rightToolbarWidth,
            height: rightToolbarHeight
        } = rightToolbarTracker;

        const {
            height: externalContentHeight
        } = new NodeTracker({ node: this.externalContentNode });

        this.hasExternalContent = ko.pureComputed(() => externalContentHeight() > 0);

        const toolbarSize = ko.pureComputed(() => _([leftToolbarHeight(), rightToolbarHeight()]).max());
        const isPinnedToolbar = ko.pureComputed(() => this.toolbar.left.isPinned() && this.toolbar.right.isPinned());
        const isAnimationAllowed = ko.observable(false);

        this.minHeight = toolbarSize;

        this.bodyPadding = ko.pureComputed(() => {
            if (isPinnedToolbar()) {
                const leftToolbarSize = leftToolbarWidth();
                const rightToolbarSize = rightToolbarWidth();

                return `0 ${rightToolbarSize}px 0 ${leftToolbarSize}px`;
            }
        }).extend({ deferred: true });

        this.bodyMargin = ko.pureComputed(() => {
            if (!isPinnedToolbar()) {
                if (this.toolbar.placement === 'top')
                    return `${toolbarSize()}px 0 0 0`;
                else
                    return `0 0 ${toolbarSize()}px 0`;
            }
        }).extend({ deferred: true });

        const extrasMargin = {
            left: ko.pureComputed(() => isPinnedToolbar() ? -leftToolbarWidth() : 0).extend({ deferred: true }),
            right: ko.pureComputed(() => isPinnedToolbar() ? -rightToolbarWidth() : 0).extend({ deferred: true })
        }

        this.content = ko.pureComputed(() => ko.ignoreDependencies((changesCount) => quillWrapper.getContent(), this, [this.changesCount()]));

        this.attachmentsGrid = {
            source: this.attachmentsAndUploads,
            columnWidth: 100,
            offset: 5,
            threshold: 50
        }

        this.isReady = ko.pureComputed(() => {
            return this.quill() != undefined &&
                this.leftToolbarNode() != undefined &&
                this.rightToolbarNode() != undefined;
        });

        this.hasMedia = ko.pureComputed(() => this.attachmentsAndUploads().length > 0);

        effects.register([
            this.imagesUploader.uploads.onArrayChange({ add: () => this.onUploadAdded() }),
            isPinnedToolbar.subscribe(() => isAnimationAllowed(true)),

            this.isReady.subscribe(() => {
                requestAnimationFrame(() => {
                    leftToolbarTracker.update(true);
                    rightToolbarTracker.update(true);
                });
            })
        ]);

        effects.register((quill, focusRequired) => {
            if (quill != undefined) {
                if (focusRequired && !quill.hasFocus())
                    quill.focus();
                else if (!focusRequired && quill.hasFocus())
                    quill.blur();
            }
        }, [this.quill, this.hasFocus]);

        effects.register(node => {
            if (node) {
                ko.applyBindingsToNode(node, {
                    'style': {
                        'margin-left': extrasMargin.left.toPx(),
                        'margin-right': extrasMargin.right.toPx()
                    }
                }, this);
            }
        }, [this.extrasNode]);

        effects.register(new AutofocusManager({
            enable: isAutoFocusEnabled,
            focus: () => this.focus()
        }));      

        ko.applyBindingsToNode(info.element, {
            'css': {
                'chat-editor': true,
                'chat-editor--with-background': hasBackground,
                'chat-editor--animated': isAnimationAllowed,
                'chat-editor--compact': isPinnedToolbar,
                'chat-editor--toolbar-top': this.toolbar.placement === 'top',
                'chat-editor--toolbar-bottom': this.toolbar.placement === 'bottom'
            },

            'attr': {
                'translate': 'no'
            }
        });

        ko.applyBindingsToNode(info.element, {
            'css': `chat-editor--${theme()}-theme`
        });

        this.isReady.when().then(() => {
            if (config.init)
                config.init(this);
        });
    }

    pickImages = async () => {
        const files = await FilesHelpers.pickImages();
        this.uploadImages(files);
    }

    uploadImages(files: File[]) {
        files.forEach(file => {
            if (FilesHelpers.isImage(file))
                this.upload(file);
        })
    }

    removeAttachment = (attachment: any) => {
        this.attachments.remove(attachment);
    }

    setText = (content: string, focus = true) => {
        this.quill.invokeNotNull(quill => {
            quill.setText(content);

            if (focus) {
                quill.focus();
                quill.setSelection(quill.getLength(), 0);
            }

            quill.history.clear();
        });
    }

    setContent = (html: string, focus = true) => {
        html = html.replace(/[\n\r]/g, '</br>');

        this.quill.invokeNotNull(quill => {
            const delta = quill.clipboard.convert(html);

            quill.setContents(delta);

            if (focus) {
                quill.focus();
                quill.setSelection(quill.getLength(), 0);
            }

            quill.history.clear();
        });
    }

    insertEmoji = (emoji: EmojisPickerEmoji) => {
        this.quill.invokeNotNull(quill => {
            const selection = quill.getSelection(true);

            if (selection) {
                quill.deleteText(selection.index, selection.length);
                quill.insertEmbed(selection.index, 'wcc-emoji', emoji.unicode());
                quill.setSelection(selection.index + 1, 0);
            }
        });
    }

    insertBlot = (blot: IChatEditorBlot, value: any, onInsert?: (blot: IChatEditorBlot) => void) => {
        this.quill.invokeNotNull(quill => {
            const selection = quill.getSelection(true);

            if (selection) {
                quill.deleteText(selection.index, selection.length);
                const st = quill.insertEmbed(selection.index, blot.blotName, value);
                quill.setSelection(selection.index + 1, 0);
                
                //if(onInsert)
                //    onInsert(blot);
            }
        });
    }

    enable = (enable: boolean) => {
        this.quill.invokeNotNull(quill => quill.enable(enable));
    }

    reset = (focus = true) => {
        this.imagesUploader.reset();
        this.attachments([]);
        this.setText('', focus);
    }

    clearHistory = () => {
        this.quill.invokeNotNull(quill => quill.history.clear());
    }

    focus = () => {
        this.quill.invokeNotNull(quill => {
            if (!this.hasFocus())
                quill.focus();
        });
    }

    blur = () => {
        this.quill.invokeNotNull(quill => {
            if (this.hasFocus())
                quill.blur();
        });
    }

    onToolsMouseUp = (m: any, e: JQueryEventObject) => {
        this.cancelEvent(e);
    }

    onToolsMouseDown = (m: any, e: JQueryEventObject) => {
        this.cancelEvent(e);
    }    

    private async upload(file: File) {
        const wccImage = await this.imagesUploader.upload(file);

        if (wccImage != undefined)
            this.attachments.push(wccImage);
    }

    private scrollToAttachments() {
        var bodyNode = this.bodyNode(),
            attachmentsNode = this.attachmentsNode();

        if (bodyNode && attachmentsNode) {
            var bodyRect = bodyNode.getBoundingClientRect(),
                attachmentsRect = attachmentsNode.getBoundingClientRect();

            var bottom = attachmentsRect.bottom - bodyRect.top,
                top = bottom - attachmentsFocusAreaSize;

            if (top < 0 || top > attachmentsFocusAreaSize)
                $(bodyNode).animate({ scrollTop: bodyNode.scrollTop + top });
        }
    }

    private onUploadAdded() {
        requestAnimationFrame(() => this.scrollToAttachments());
    }

    private getTheme(config: ChatEditorVMConfig): ChatEditorVMTheme {
        if (ko.unwrap(config.noBorder) ?? false)
            return 'noborder';
        else
            return ko.unwrap(config.theme) ?? 'classic';
    }

    private buildToolbar(toolbarConfig?: ChatEditorToolbarConfig): ChatEditorToolbar {
        const left = this.buildToolbarSection(toolbarConfig?.left ?? []);
        const right = this.buildToolbarSection(toolbarConfig?.right ?? []);

        const placement = toolbarConfig?.placement ?? 'bottom';

        return { left, right, placement }
    }

    private buildToolbarSection(groupsConfig: Array<ChatEditorToolsGroupConfig>): ChatEditorToolbarSection {
        const groups = groupsConfig.map(groupConfig => this.buildToolsGroup(groupConfig));

        const isVisible = ko.pureComputed(() => groups.length > 0 && groups.reduce((result, group) => result && group.isVisible(), true));
        const isPinned = ko.pureComputed(() => groups.reduce((result, group) => result && (!group.isVisible() || group.isPinned()), true));

        return { groups, isVisible, isPinned }
    }

    private buildToolsGroup(groupConfig?: ChatEditorToolsGroupConfig): ChatEditorToolsGroup {
        const toolsConfig = groupConfig?.tools;

        var tools: Array<Tool> = [];

        if (_.isFunction(toolsConfig))
            tools = toolsConfig(this);
        else if (_.isArray(toolsConfig))
            tools = toolsConfig;

        const isVisible = ko.pureComputed(() => tools.some(tool => tool.isVisible()) && ko.unwrap(groupConfig?.isVisible ?? true));
        const isPinned = ko.observable(groupConfig?.isPinned).unwrap().default(false);
        const css = ko.observable(groupConfig?.css).unwrap().default('');

        return { tools, isVisible, isPinned, css }
    }

    private cancelEvent(e: JQueryEventObject) {
        e.preventDefault();
        e.stopPropagation();
    }    
}